import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { TbCircleCheck } from 'react-icons/tb';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { PageState } from '../components/PageState';
import { ExerciseGif } from '../training/ExerciseGif';
import { formatDate, formatKg, formatKgNumber } from '../lib/format';
import { createTrainingApi } from '../training/training-api';
import { useAutosave } from './use-autosave';
import './member.css';
import './train-progress.css';

const trainingApi = createTrainingApi();

function toNonNegativeInt(value) {
  const num = parseInt(value, 10);
  return Number.isFinite(num) && num >= 0 ? num : 0;
}

function toNonNegativeNumber(value) {
  const num = Number(value);
  return Number.isFinite(num) && num >= 0 ? num : 0;
}

// Mirrors MyRoutinePage's own countLabel: the day picker shows the same
// "N ejercicios" subtitle as "Mi rutina"'s day tabs, so the two pickers read
// as one consistent block-card pattern rather than two different widgets.
function countLabel(count) {
  return count === 1 ? '1 ejercicio' : `${count} ejercicios`;
}

function emptyRows(count) {
  return Array.from({ length: Math.max(1, count || 1) }, (_, index) => ({ setNumber: index + 1, reps: '', weightKg: '' }));
}

// Un 0 guardado (el autosave manda las filas vacías como 0/0) vuelve como campo vacío, así se
// ve el placeholder y no hay que borrar un "0" antes de tipear.
function fieldFromStored(value) {
  return Number(value) > 0 ? String(value) : '';
}

function rowsFromSets(sets) {
  return [...sets]
    .sort((a, b) => a.setNumber - b.setNumber)
    .map((set) => ({ setNumber: set.setNumber, reps: fieldFromStored(set.reps), weightKg: fieldFromStored(set.weightKg) }));
}

function setLabel(set) {
  return `${formatKgNumber(set.weightKg)}×${set.reps}`;
}

// `2026-10-13` -> `13/10` (corte de string; el servidor es la autoridad de las fechas).
function shortDate(value) {
  const full = formatDate(value);
  return full ? full.slice(0, 5) : '';
}

/**
 * `/mi-cuenta/entrenar` (spec "Entrenar", plan Task 9): pick a day of the
 * member's own active routine, `startSession` it (idempotent — reloading
 * mid-workout resumes the same `in_progress` session the server already has),
 * then one row per set per exercise with big `inputMode` inputs for kg/reps.
 *
 * Prefill priority per exercise: (1) the session ITSELF already has saved
 * sets for it (resuming after a reload or an earlier autosave) —
 * those win, since they are literally this workout's current state; (2)
 * otherwise the most recent entry of `getExerciseHistory` (what counts as history is the
 * server's call);
 * (3) otherwise empty rows, one per `item.sets` (the routine's prescribed
 * count). "+ serie" always appends one more empty row regardless of source. When an exercise
 * has history, each row shows the previous time's same-numbered set in an "Antes" column and
 * a "Ver historial" toggle lists up to the last 5 entries (no extra request).
 *
 * Every change to a kg/reps field schedules an autosave (debounced, see `useAutosave`); it also
 * flushes when the page is hidden, the day changes or the screen unmounts. Preloading never saves.
 *
 * "Terminar entrenamiento" first settles any pending/in-flight autosave, re-sends the current
 * rows (so nothing typed since the last autosave is lost) and then completes the session; the
 * summary's volume figure is `GET /me/progress`'s own current-week
 * `volumeKg` — the server's number, never a client recomputation (spec: "el
 * cliente no calcula métricas"). If that follow-up fetch fails for a reason
 * other than an expired session, the completion itself still stands and the
 * summary is simply shown without a volume line.
 */
export function TrainPage({ api = trainingApi }) {
  const { handleApiError } = useAuth();
  const [searchParams] = useSearchParams();
  const requestedDayId = searchParams.get('dia');
  const autoStarted = useRef(false);
  const [routineState, setRoutineState] = useState({ status: 'loading', routine: null, error: '' });
  const [selectedDayId, setSelectedDayId] = useState(null);
  const [dayState, setDayState] = useState({ status: 'idle', error: '' });
  const [session, setSession] = useState(null);
  const [groups, setGroups] = useState([]);
  const [completeState, setCompleteState] = useState({ status: 'idle', error: '' });
  const [summary, setSummary] = useState(null);
  const [openHistory, setOpenHistory] = useState({});

  // The autosave reads the latest rows/session through refs, so a save fired by a timer never
  // sends stale state.
  const groupsRef = useRef([]);
  const sessionRef = useRef(null);
  groupsRef.current = groups;
  sessionRef.current = session;

  const autosave = useAutosave({
    enabled: dayState.status === 'ready' && Boolean(session) && session.status !== 'completed',
    save: async () => {
      await api.saveSessionSets(sessionRef.current.id, buildSetsPayload(groupsRef.current));
    },
    onError: (caught) => handleApiError(caught, {}).handled,
  });

  useEffect(() => {
    const flushNow = () => { autosave.flush(); };
    const onVisibility = () => { if (document.visibilityState === 'hidden') flushNow(); };
    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('pagehide', flushNow);
    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('pagehide', flushNow);
      flushNow();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autosave.flush]);

  const loadRoutine = useCallback(async () => {
    setRoutineState({ status: 'loading', routine: null, error: '' });
    try {
      const routine = await api.getMyRoutine();
      setRoutineState({ status: routine ? 'ready' : 'empty', routine, error: '' });
    } catch (caught) {
      const outcome = handleApiError(caught, {
        clearBusinessData: () => setRoutineState({ status: 'loading', routine: null, error: '' }),
      });
      if (outcome.handled) return;
      setRoutineState({ status: 'error', routine: null, error: errorMessageFor(caught) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, handleApiError]);

  useEffect(() => {
    loadRoutine();
  }, [loadRoutine]);

  const selectDay = useCallback(async (day) => {
    // Save what is pending for the previous day while sessionRef still points at it.
    // If that save fails (not a handled 401) stay on the current day: error + Retry stay visible.
    if (!(await autosave.flush())) return;
    autosave.reset();
    setSelectedDayId(day.id);
    setDayState({ status: 'starting', error: '' });
    setSession(null);
    setGroups([]);
    setSummary(null);
    setOpenHistory({});
    setCompleteState({ status: 'idle', error: '' });
    try {
      const startedSession = await api.startSession({ routineDayId: day.id });
      // Grouped by routineItemId first: two items can share the same effective exercise (the coach
      // repeated it, or a member's substitution landed on it), so grouping by exerciseId alone
      // would hand BOTH items every saved set of that exercise. routineItemId-less sets (legacy
      // rows saved before this existed) fall back to exerciseId.
      const setsByItemId = new Map();
      const setsByExerciseIdFallback = new Map();
      (startedSession.sets || []).forEach((set) => {
        if (set.routineItemId) {
          if (!setsByItemId.has(set.routineItemId)) setsByItemId.set(set.routineItemId, []);
          setsByItemId.get(set.routineItemId).push(set);
        } else {
          if (!setsByExerciseIdFallback.has(set.exerciseId)) setsByExerciseIdFallback.set(set.exerciseId, []);
          setsByExerciseIdFallback.get(set.exerciseId).push(set);
        }
      });
      const builtGroups = await Promise.all(day.items.map(async (item) => {
        const history = (await api.getExerciseHistory(item.exercise.id)) || [];
        const existing = setsByItemId.get(item.id) || setsByExerciseIdFallback.get(item.exercise.id);
        if (existing && existing.length > 0) return { item, rows: rowsFromSets(existing), history };
        if (history.length > 0) return { item, rows: rowsFromSets(history[0].sets), history };
        return { item, rows: emptyRows(item.sets), history };
      }));
      setSession(startedSession);
      setGroups(builtGroups);
      setDayState({ status: 'ready', error: '' });
    } catch (caught) {
      const outcome = handleApiError(caught, {});
      if (outcome.handled) return;
      setDayState({ status: 'error', error: errorMessageFor(caught) });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, handleApiError, autosave.flush, autosave.reset]);

  // "Entrenar" desde Mi rutina llega con ?dia=<id>: arranca ese día una sola vez. Después manda
  // el selector, así "Entrenar otro día" no vuelve a abrir el mismo.
  useEffect(() => {
    if (autoStarted.current || routineState.status !== 'ready' || !requestedDayId) return;
    autoStarted.current = true;
    const day = routineState.routine.days.find((entry) => entry.id === requestedDayId);
    if (day && (day.items || []).length > 0) selectDay(day);
  }, [routineState, requestedDayId, selectDay]);

  function updateRow(itemId, rowIndex, field, value) {
    setGroups((current) => current.map((group) => (
      group.item.id === itemId
        ? { ...group, rows: group.rows.map((row, index) => (index === rowIndex ? { ...row, [field]: value } : row)) }
        : group
    )));
    autosave.markDirty();
  }

  function addRow(itemId) {
    setGroups((current) => current.map((group) => (
      group.item.id === itemId
        ? { ...group, rows: [...group.rows, { setNumber: group.rows.length + 1, reps: '', weightKg: '' }] }
        : group
    )));
    autosave.markDirty();
  }

  // Quita la fila y renumera 1..n para no dejar huecos; siempre queda al menos una fila.
  function removeRow(itemId, rowIndex) {
    setGroups((current) => current.map((group) => (
      group.item.id === itemId && group.rows.length > 1
        ? {
          ...group,
          rows: group.rows
            .filter((_, index) => index !== rowIndex)
            .map((row, index) => ({ ...row, setNumber: index + 1 })),
        }
        : group
    )));
    autosave.markDirty();
  }

  function buildSetsPayload(currentGroups = groups) {
    return currentGroups.flatMap((group) => group.rows.map((row) => ({
      routineItemId: group.item.id,
      exerciseId: group.item.exercise.id,
      setNumber: row.setNumber,
      reps: toNonNegativeInt(row.reps),
      weightKg: toNonNegativeNumber(row.weightKg),
    })));
  }

  // Nudge (not a hard block server-side): every row still at 0/0 almost
  // always means the member hasn't typed anything yet, so "Terminar" stays
  // disabled with a short hint instead of completing an empty-looking set.
  const hasAnyNonZeroSet = groups.some((group) => group.rows.some(
    (row) => toNonNegativeInt(row.reps) > 0 || toNonNegativeNumber(row.weightKg) > 0,
  ));

  async function handleComplete() {
    if (!session) return;
    setCompleteState({ status: 'completing', error: '' });
    try {
      await autosave.settle();
      const saved = await api.saveSessionSets(session.id, buildSetsPayload(groupsRef.current));
      const completed = await api.completeSession(saved.id);
      setSession(completed);
      let weekVolumeKg = null;
      try {
        const progress = await api.getMyProgress();
        const weeks = (progress && progress.weekly) || [];
        const lastWeek = weeks[weeks.length - 1];
        weekVolumeKg = lastWeek ? lastWeek.volumeKg : null;
      } catch (progressError) {
        if (progressError && progressError.status === 401) throw progressError;
        weekVolumeKg = null;
      }
      setSummary({ session: completed, weekVolumeKg });
      setCompleteState({ status: 'done', error: '' });
    } catch (caught) {
      autosave.unlock();
      autosave.markDirty();
      const outcome = handleApiError(caught, {});
      if (outcome.handled) return;
      setCompleteState({ status: 'error', error: errorMessageFor(caught) });
    }
  }

  function resetToDayPicker() {
    autosave.reset();
    setSelectedDayId(null);
    setDayState({ status: 'idle', error: '' });
    setSession(null);
    setGroups([]);
    setCompleteState({ status: 'idle', error: '' });
    setSummary(null);
    setOpenHistory({});
  }

  if (routineState.status === 'loading') {
    return <PageState title="Entrenar" status="loading" />;
  }

  if (routineState.status === 'error') {
    return (
      <PageState
        title="Entrenar"
        status="error"
        message={routineState.error}
        action={<button type="button" className="gym-button gym-button--secondary" onClick={loadRoutine}>Reintentar</button>}
      />
    );
  }

  if (routineState.status === 'empty') {
    return <PageState title="Entrenar" status="empty" message="Todavía no tenés una rutina asignada. Pedile a tu profesor que te arme una." />;
  }

  const { routine } = routineState;

  return (
    <section className="gym-page" aria-labelledby="gym-page-title">
      <header className="gym-page__header">
        <h1 id="gym-page-title" className="gym-page__title">Entrenar</h1>
      </header>

      <div className="gym-train-days" role="tablist" aria-label="Días de tu rutina">
        {routine.days.map((day) => (
          <button
            key={day.id}
            type="button"
            role="tab"
            aria-selected={day.id === selectedDayId}
            className={`gym-train-day${day.id === selectedDayId ? ' is-active' : ''}`}
            onClick={() => selectDay(day)}
          >
            <span className="gym-train-day__name">{day.name}</span>
            <span className="gym-train-day__count">{countLabel((day.items || []).length)}</span>
          </button>
        ))}
      </div>

      {dayState.status === 'starting' && (
        <p role="status" className="gym-state__text">Preparando tu entrenamiento…</p>
      )}
      {dayState.status === 'error' && (
        <p role="alert" className="gym-form__error">{dayState.error}</p>
      )}

      {dayState.status === 'ready' && session && session.status !== 'completed' && (
        <div className="gym-train-session">
          {groups.map(({ item, rows, history }) => {
            const previousBySet = new Map(((history[0] && history[0].sets) || []).map((set) => [set.setNumber, set]));
            const hasHistory = history.length > 0;
            const rowClass = `gym-train-row${hasHistory ? ' gym-train-row--with-previous' : ''}`;
            return (
              <article key={item.id} className="gym-train-exercise">
                <header className="gym-train-exercise__header">
                  <span className="gym-train-exercise__media"><ExerciseGif exercise={item.exercise} size="sm" /></span>
                  <span className="gym-train-exercise__info">
                    <span className="gym-train-exercise__name">{item.exercise.name}</span>
                    <span className="gym-train-exercise__meta">{item.sets} × {item.reps}</span>
                  </span>
                </header>
                <div className="gym-train-rows">
                  <div className={`${rowClass} gym-train-row--headings`} aria-hidden="true">
                    <span />
                    {hasHistory && <span className="gym-train-column-heading">Antes</span>}
                    <span className="gym-train-column-heading">kg</span>
                    <span className="gym-train-column-heading">reps</span>
                    <span />
                  </div>
                  {rows.map((row, index) => {
                    const kgId = `gym-train-kg-${item.id}-${index}`;
                    const repsId = `gym-train-reps-${item.id}-${index}`;
                    return (
                      <div className={rowClass} key={index}>
                        <span className="gym-train-row__label">Serie {row.setNumber}</span>
                        {hasHistory && (
                          <span className="gym-train-row__previous">
                            {previousBySet.has(row.setNumber) ? setLabel(previousBySet.get(row.setNumber)) : '—'}
                          </span>
                        )}
                        <label className="gym-sr-only" htmlFor={kgId}>{`Serie ${row.setNumber} de ${item.exercise.name} — peso en kilogramos`}</label>
                        <input
                          id={kgId}
                          className="gym-input gym-train-row__input"
                          type="text"
                          inputMode="decimal"
                          placeholder="kg"
                          value={row.weightKg}
                          onChange={(event) => updateRow(item.id, index, 'weightKg', event.target.value)}
                        />
                        <label className="gym-sr-only" htmlFor={repsId}>{`Serie ${row.setNumber} de ${item.exercise.name} — repeticiones`}</label>
                        <input
                          id={repsId}
                          className="gym-input gym-train-row__input"
                          type="text"
                          inputMode="numeric"
                          placeholder="reps"
                          value={row.reps}
                          onChange={(event) => updateRow(item.id, index, 'reps', event.target.value)}
                        />
                        <button
                          type="button"
                          className="gym-train-row__remove"
                          aria-label={`Eliminar serie ${row.setNumber} de ${item.exercise.name}`}
                          disabled={rows.length <= 1}
                          onClick={() => removeRow(item.id, index)}
                        >
                          <span aria-hidden="true">✕</span>
                        </button>
                      </div>
                    );
                  })}
                </div>
                <button
                  type="button"
                  className="gym-button gym-button--secondary gym-train-add-set"
                  onClick={() => addRow(item.id)}
                >
                  + Serie
                </button>
                {hasHistory && (
                  <button
                    type="button"
                    className="gym-button gym-button--secondary gym-train-history-toggle"
                    aria-expanded={Boolean(openHistory[item.id])}
                    onClick={() => setOpenHistory((current) => ({ ...current, [item.id]: !current[item.id] }))}
                  >
                    {openHistory[item.id] ? 'Ocultar historial' : 'Ver historial'}
                  </button>
                )}
                {hasHistory && openHistory[item.id] && (
                  <div className="gym-train-history" role="region" aria-label={`Historial de ${item.exercise.name}`}>
                    {history.map((entry, entryIndex) => (
                      <div className="gym-train-history__entry" key={`${entry.performedOn}-${entryIndex}`}>
                        <span className="gym-train-history__date">{shortDate(entry.performedOn)}</span>
                        <span className="gym-train-history__sets">{entry.sets.map(setLabel).join(' · ')}</span>
                      </div>
                    ))}
                  </div>
                )}
              </article>
            );
          })}

          <div className="gym-train-savestate" role="status" aria-live="polite">
            {autosave.status === 'saving' && 'Guardando…'}
            {autosave.status === 'saved' && 'Guardado'}
            {autosave.status === 'error' && (
              <>
                <span className="gym-train-savestate__error">No se pudo guardar</span>
                <button type="button" className="gym-button gym-button--secondary" onClick={() => autosave.flush()}>Reintentar</button>
              </>
            )}
          </div>
          {completeState.status === 'error' && <p role="alert" className="gym-form__error">{completeState.error}</p>}
          {!hasAnyNonZeroSet && (
            <p className="gym-state__text">Cargá al menos un peso o una repetición para poder terminar.</p>
          )}

          <div className="gym-train-actions gym-train-actions--fixed">
            <button
              type="button"
              className="gym-button gym-button--primary"
              onClick={handleComplete}
              disabled={completeState.status === 'completing' || !hasAnyNonZeroSet}
            >
              {completeState.status === 'completing' ? 'Terminando…' : 'Terminar entrenamiento'}
            </button>
          </div>
        </div>
      )}

      {summary && (
        <div className="gym-train-summary" role="status">
          <div className="gym-train-summary__content">
            <span className="gym-train-summary__check"><TbCircleCheck className="gym-train-summary__icon" aria-hidden="true" /></span>
            <h2 className="gym-train-summary__title">Entrenamiento completado</h2>
            <p className="gym-train-summary__line">{summary.session.dayName} · {formatDate(summary.session.performedOn)}</p>
            <div className="gym-train-summary__stats">
              <div className="gym-train-summary__stat">
                <span className="gym-train-summary__stat-value">{summary.session.sets.length}</span>
                <span className="gym-train-summary__stat-label">series registradas</span>
              </div>
              {summary.weekVolumeKg !== null && (
                <div className="gym-train-summary__stat">
                  <span className="gym-train-summary__stat-label">Volumen esta semana: </span>
                  <span className="gym-train-summary__stat-value">{formatKg(summary.weekVolumeKg)}</span>
                </div>
              )}
            </div>
          </div>
          <button
            type="button"
            className="gym-button gym-button--primary gym-train-summary__action"
            onClick={resetToDayPicker}
          >
            Entrenar otro día
          </button>
        </div>
      )}
    </section>
  );
}
